import { Alert } from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconExclamationCircleFilled,
} from "@tabler/icons-react";
import type { ReactElement } from "react";
import type { BulkClientAlertType } from "@/pages/TaxProClientList/components/BulkClientModal/types";

type BulkClientAlertBoxProps = {
  /**
   * Distinguishes the type of alert.
   */
  type: BulkClientAlertType;
  /**
   * The message to display.
   */
  message: string;
  /**
   * Called whenever alert box is clicked.
   */
  onClick?: () => void;
  /**
   * Element to show on screen.
   */
  children?: ReactElement | null;
};

export const BulkClientModalAlert: React.FC<BulkClientAlertBoxProps> = ({
  type,
  message,
  onClick,
  children,
}) => {
  const [color, icon] = getAlertDetails(type);

  return (
    <Alert
      icon={icon}
      color={color}
      variant="light"
      mb="sm"
      style={{ marginTop: 10 }}
      onClick={onClick}
    >
      {message}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
        }}
      >
        {children}
      </div>
    </Alert>
  );
};

/**
 * Determines the alert color and icon based on the type of alert.
 * @param type
 */
const getAlertDetails = (
  type: BulkClientAlertType
): [string, ReactElement?] => {
  // Icons have keys because sonarlint thinks this is a list...
  switch (type) {
    case "error":
      return [
        "red",
        <IconExclamationCircleFilled size={20} key="error-icon" />,
      ];
    case "notice":
      return [
        "rgba(179, 179, 179, 1)",
        <IconAlertCircle size={20} key="notice-icon" />,
      ];
    case "warning":
      return ["yellow", <IconAlertTriangle size={20} key="warning-icon" />];
    case "success":
      return ["green", <IconCheck size={20} key="success-icon" />];
    case "info":
      return ["blue", <IconAlertCircle size={20} key="info-icon" />];
    default:
      return ["grey", <IconAlertCircle size={20} key="default-icon" />];
  }
};
